import {
  RAW_MATERIAL_CATEGORIES,
  RAW_MATERIAL_CATEGORY_LABELS,
  type RawMaterial,
} from "@artisancode/api-types";
import { useWatch } from "react-hook-form";

import { SearchableSelect } from "@/components/shared/searchable-select";
import { WorkspaceCreateField } from "@/components/shared/workspace-create-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useNextNumberPreview } from "@/hooks/use-number-sequences";

import { RawMaterialKindFields } from "./raw-material-kind-fields";
import { formatCostPerUnit, packSize } from "./raw-material-utils";
import { useRawMaterialForm } from "./use-raw-material-form";

export function RawMaterialDialog({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: RawMaterial | null;
}) {
  const { form, uomList, ingredientList, conversions, isSaving, onSubmit } =
    useRawMaterialForm(open, editing, () => onOpenChange(false));
  const { preview: sequencePreview, autoGenerate } =
    useNextNumberPreview("raw_material");

  const isUomLocked = !!editing && Boolean(editing.hasMovements);
  const kind = useWatch({ control: form.control, name: "kind" });
  const uomId = useWatch({ control: form.control, name: "uomId" });
  const ingredientId = useWatch({
    control: form.control,
    name: "ingredientId",
  });
  const ingredient = ingredientList.find((i) => i.id === ingredientId);
  // A package unit must convert to the ingredient's unit; that conversion is the pack size.
  const packUoms =
    kind === "purchased" && ingredient
      ? uomList.filter(
          (u) =>
            u.id === uomId ||
            packSize(conversions, u.id, ingredient.uomId) != null,
        )
      : uomList;
  const size =
    kind === "purchased" && ingredient && uomId
      ? packSize(conversions, uomId, ingredient.uomId)
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Edit Bahan Baku" : "Tambah Bahan Baku"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            id="raw-material-form"
            onSubmit={onSubmit}
            className="grid gap-3 py-2"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nama Bahan Baku</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={
                        kind === "purchased"
                          ? "Contoh: Susu Diamond UHT 1L"
                          : kind === "ingredient"
                            ? "Contoh: Susu UHT"
                            : "Contoh: Cup 12oz"
                      }
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <RawMaterialKindFields
              form={form}
              ingredientList={ingredientList}
              kindLocked={isUomLocked}
              disabled={isSaving}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="sku"
                render={({ field }) => {
                  const previewSeq =
                    !editing && autoGenerate ? sequencePreview : "";
                  return (
                    <FormItem>
                      <FormLabel>Kode / SKU</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={
                            editing
                              ? "SKU Bahan Baku"
                              : previewSeq
                                ? `Otomatis: ${previewSeq}`
                                : "Opsional"
                          }
                          {...field}
                          disabled={!!editing || autoGenerate}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Kategori</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Pilih kategori" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {RAW_MATERIAL_CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {RAW_MATERIAL_CATEGORY_LABELS[cat]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="uomId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {kind === "purchased" ? "Satuan Kemasan" : "Satuan (UOM)"}
                    </FormLabel>
                    <FormControl>
                      <SearchableSelect
                        value={field.value}
                        onChange={field.onChange}
                        options={packUoms.map((u) => ({
                          value: u.id,
                          label: `${u.name} (${u.symbol})${!u.isActive ? " (Nonaktif)" : ""}`,
                        }))}
                        placeholder="Pilih satuan..."
                        disabled={isUomLocked || isSaving}
                      />
                    </FormControl>
                    {kind === "purchased" && ingredient && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {size != null
                          ? `Isi per kemasan: ${size.toLocaleString("id-ID")} ${ingredient.uom?.symbol ?? ""} (dari Konversi Satuan)`
                          : uomId
                            ? "Satuan ini belum punya konversi ke satuan bahan resep. Tambahkan di Konversi Satuan."
                            : "Hanya satuan yang punya konversi ke satuan bahan resep yang tampil."}
                      </p>
                    )}
                    {isUomLocked && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Satuan tidak dapat diubah karena bahan baku sudah
                        memiliki riwayat pergerakan stok.
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {editing && (
                <FormItem>
                  <FormLabel>Harga (dihitung sistem)</FormLabel>
                  <p className="flex h-9 items-center text-sm font-medium">
                    {formatCostPerUnit(editing)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Terbentuk dari Stok Awal, belanja, atau repacking.
                  </p>
                </FormItem>
              )}
            </div>

            <FormField
              control={form.control}
              name="minStock"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Batas Minimum Stok (Opsional)</FormLabel>
                  <FormControl>
                    <NumberInput
                      placeholder="Contoh: 5"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Catatan (Opsional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Keterangan tambahan bahan baku..."
                      className="resize-none"
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <WorkspaceCreateField
              control={form.control}
              name="workspaceId"
              editing={!!editing}
            />
          </form>
        </Form>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Batal
          </Button>
          <Button type="submit" form="raw-material-form" disabled={isSaving}>
            {isSaving
              ? "Menyimpan..."
              : editing
                ? "Simpan Perubahan"
                : "Tambah"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
